/**
 * Dashboard Layout Component
 * ===========================
 * Main layout wrapper with sidebar and header
 */

import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import BrainChatPanel from '../brain/BrainChatPanel';
import { useOrg } from '../../hooks';
import { DEFAULT_BRAND_NAME } from '../../utils/constants';

const DashboardLayout = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { activeOrg, loading: orgLoading } = useOrg();

  // Brand the browser tab (title + favicon) for the active organization.
  // The active org (Zamanat, Zignally, ...) is a tenant, so we show its name and
  // logo — including for super admins, who see whichever org they've switched
  // into. With no active org we fall back to the platform brand + default icon.
  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']");
    const defaultFavicon = `${process.env.PUBLIC_URL || ''}/images/favicon.png`;

    if (activeOrg) {
      document.title = `${activeOrg.name} Admin Portal`;
      // Prefer the org's dedicated favicon; fall back to its logo, then the platform default.
      if (link) link.setAttribute('href', activeOrg.favicon_url || activeOrg.logo_url || defaultFavicon);
    } else {
      document.title = `${DEFAULT_BRAND_NAME} Admin Portal`;
      if (link) link.setAttribute('href', defaultFavicon);
    }
  }, [activeOrg]);

  // Mobile drawer: lock background scroll and allow Esc to dismiss while open.
  useEffect(() => {
    if (!mobileOpen) return;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-[#F9FAFB] dark:bg-[#1A1C1E]" style={{ '--sidebar-width': isCollapsed ? '4rem' : '16rem' }}>
      {/* Sidebar */}
      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(prev => !prev)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content Area - margin shifts based on sidebar state */}
      <div className={`${isCollapsed ? 'md:ml-16' : 'md:ml-64'} flex flex-col min-h-screen transition-all duration-300`}>
        {/* Header */}
        <Header onMobileMenuOpen={() => setMobileOpen(true)} />

        {/* Page Content — key forces full remount on org switch.
            Hold children until the active org has resolved so they mount once
            under the real org key. Otherwise the key transitions from the
            'no-org' sentinel to the org id on first load (super admins resolve
            the org via two async calls), remounting the whole page and making
            it flash/reload its data. `loading` is only true during initial
            resolution — an explicit org switch uses `switching`, so switches
            still remount via the key as intended. */}
        <main className="flex-1 min-w-0 p-6" key={activeOrg?.id || 'no-org'}>
          {orgLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : children}
        </main>
      </div>

      {/* Global Brain Assistant — hidden while the mobile nav drawer is open */}
      {!chatOpen && !mobileOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:bg-primary-600 hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
          title="Zamanat Brain Assistant"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </button>
      )}

      <BrainChatPanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
      />
    </div>
  );
};

export default DashboardLayout;
